import React, { useMemo } from 'react'
import { CANVAS_TEXT_PALETTE, canvasTextInlineStyle, parseCanvasText, type CanvasTextColorPalette, type CanvasTextInline } from '../utils/canvasTextContent'
import { canvasTextMarkerClass, canvasTextMarkerData, canvasTextMarkerLook, canvasTextMarkerRuns, canvasTextMarkerVars, canvasTextShapedMarkerClass } from '../utils/canvasTextMarker'

/** Zählt die **Markerstriche** des Markers über den ganzen Text — jede Zeile setzt fort. */
interface StrokeCounter { next: number }

/**
 * Eine Zeile mit ihren Auszeichnungen — Klassen für die Art, Stile für die
 * Werte. Ein Stück mit Marker bekommt die Klassen seines Markerstrichs
 * (`canvasTextMarker.ts`); die Form zeichnet die SCSS.
 */
function renderInline(parts: CanvasTextInline[], seed: string, strokes: StrokeCounter, palette: CanvasTextColorPalette): React.ReactNode[] {
  const { pieces, next } = canvasTextMarkerRuns(
    parts.map(part => (part.kind === 'text' ? part.marks.highlight ?? null : null)),
    strokes.next,
    parts.map(part => (part.kind === 'text' ? part.marks.look : undefined)),
  )
  strokes.next = next
  return parts.map((part, index) => {
    if (part.kind === 'break') return <br key={index} />
    const { marks } = part
    const piece = pieces[index]
    const style = canvasTextInlineStyle(marks, palette) as React.CSSProperties
    const look = piece ? canvasTextMarkerLook(seed, piece) : null
    if (piece && look) Object.assign(style, canvasTextMarkerVars(piece, look, part.text, palette.markerShape))
    const className = [
      marks.bold && 'text-node__mark--bold',
      marks.italic && 'text-node__mark--italic',
      marks.underline && 'text-node__mark--underline',
      marks.strike && 'text-node__mark--strike',
      piece && look && (palette.markerShape ? canvasTextShapedMarkerClass(piece) : canvasTextMarkerClass(piece, look)),
    ].filter(Boolean).join(' ')
    if (!className && Object.keys(style).length === 0) return <React.Fragment key={index}>{part.text}</React.Fragment>
    const data = piece && look && palette.markerShape ? canvasTextMarkerData(piece, look) : {}
    return <span key={index} className={className || undefined} style={style} {...data}>{part.text}</span>
  })
}

export interface CanvasTextContentProps {
  /** Der gespeicherte Inhalt (ADR 0029). */
  content: string
  /** Woraus die Form der Marker folgt — die Kennung des Texts (`canvasTextMarkerLook`). */
  seed?: string
  /** Die Schriftfarben — der Projekttitel bringt seine eigene Palette mit. */
  palette?: CanvasTextColorPalette
}

/**
 * Der **Text** in Ruhe — der gespeicherte Inhalt, über die Whitelist gelesen
 * (`parseCanvasText`) und als React-Elemente gezeichnet. Nie `innerHTML`.
 *
 * Eine leere Zeile trägt ein `<br>`, damit sie ihre Höhe behält — sonst fiele
 * eine Leerzeile zwischen zwei Absätzen zusammen.
 */
export const CanvasTextContent: React.FC<CanvasTextContentProps> = ({ content, seed = '', palette = CANVAS_TEXT_PALETTE }) => {
  const blocks = useMemo(() => parseCanvasText(content, palette), [content, palette])
  // In Lesereihenfolge gezählt — derselbe Weg wie im Schreibfeld, also dieselben Formen.
  const strokes: StrokeCounter = { next: 0 }
  return (
    <>
      {blocks.map((block, index) => (block.kind === 'list' ? (
        <ul key={index} className="text-node__list">
          {block.items.map((item, i) => (
            <li key={i} className="text-node__list-item">{item.length > 0 ? renderInline(item, seed, strokes, palette) : <br />}</li>
          ))}
        </ul>
      ) : (
        <p key={index} className="text-node__line">
          {block.content.length > 0 ? renderInline(block.content, seed, strokes, palette) : <br />}
        </p>
      )))}
    </>
  )
}
